import {map} from "../map";
import {transform} from "ol/proj";
import $ from "jquery";
import {currentpanelgeojson, currentpanelwkt} from "./consts";
import {createEmpty, extend as OLextend, getCenter, isEmpty as isExtentEmpty} from 'ol/extent';
import {
    assignmentsource,
    assignmentsource2021,
    assignmentsource2023,
    assignmentsource2025,
    filteredInventoryLayer,
    placebasedassignmentsource,
    rotateSource
} from "../layers/activeLayers";
import {geoToH3} from "h3-js";

const EXTENT_PADDING = 25;

function isFiniteCoordinatePair(coordinates) {
    return Array.isArray(coordinates) && coordinates.length === 2 && coordinates.every((value) => Number.isFinite(value));
}

function isFiniteExtent(extent) {
    return Array.isArray(extent) && extent.length === 4 && extent.every((value) => Number.isFinite(value));
}

function hasRenderableTarget(target) {
    if (!target) {
        return false;
    }

    const {clientHeight, clientWidth} = target;

    return Number.isFinite(clientWidth) && Number.isFinite(clientHeight) && clientWidth > 0 && clientHeight > 0;
}

function normaliseExtent(extent) {
    if (!isFiniteExtent(extent)) {
        return null;
    }

    const [minX, minY, maxX, maxY] = extent;

    if (minX > maxX || minY > maxY) {
        return null;
    }

    const adjusted = extent.slice();
    const width = maxX - minX;
    const height = maxY - minY;

    if (width === 0) {
        adjusted[0] = minX - EXTENT_PADDING;
        adjusted[2] = maxX + EXTENT_PADDING;
    }

    if (height === 0) {
        adjusted[1] = minY - EXTENT_PADDING;
        adjusted[3] = maxY + EXTENT_PADDING;
    }

    return adjusted;
}

function collectSourceExtent(source) {
    if (!source) {
        return null;
    }

    const extent = createEmpty();
    let hasGeometry = false;

    if (typeof source.forEachFeature === "function") {
        source.forEachFeature((feature) => {
            if (!feature || typeof feature.getGeometry !== "function") {
                return;
            }

            const geometry = feature.getGeometry();

            if (!geometry || typeof geometry.getExtent !== "function") {
                return;
            }

            const geometryExtent = geometry.getExtent();

            if (!isFiniteExtent(geometryExtent)) {
                return;
            }

            OLextend(extent, geometryExtent);
            hasGeometry = true;
        });
    }

    if (!hasGeometry && typeof source.getExtent === "function") {
        const sourceExtent = source.getExtent();

        if (isFiniteExtent(sourceExtent)) {
            return normaliseExtent(sourceExtent);
        }

        return null;
    }

    if (!hasGeometry || isExtentEmpty(extent)) {
        return null;
    }

    return normaliseExtent(extent);
}

function getPanelCenterFromState() {
    try {
        const rawValue = currentpanelgeojson ? currentpanelgeojson.text() : null;

        if (!rawValue) {
            return null;
        }

        const parsed = JSON.parse(rawValue);
        const longitude = parsed?.location?.longitude;
        const latitude = parsed?.location?.latitude;

        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return null;
        }

        return transform([longitude, latitude], 'EPSG:4326', 'EPSG:3857');
    }
    catch (error) {
        console.warn('Unable to parse current panel geojson for center', error);
        return null;
    }
}

function buildCombinedExtent(sources = []) {
    const combinedExtent = createEmpty();
    let hasExtent = false;

    sources.forEach((source) => {
        const nextExtent = collectSourceExtent(source);

        if (!nextExtent) {
            return;
        }

        OLextend(combinedExtent, nextExtent);
        hasExtent = true;
    });

    if (!hasExtent || isExtentEmpty(combinedExtent)) {
        return null;
    }

    return normaliseExtent(combinedExtent);
}

function getSourceCenter(source) {
    const extent = collectSourceExtent(source);

    if (!extent) {
        return null;
    }

    const center = getCenter(extent);

    return isFiniteCoordinatePair(center) ? center : null;
}

function scheduleViewFit(extent, options = {}) {
    const {
        preserveZoom = false,
        preserveView = false,
        targetCenter = null,
        fallbackCenter = null
    } = options;

    if (!extent || preserveView) {
        return;
    }

    const view = map.getView();

    if (!view) {
        return;
    }

    const target = map.getTargetElement();

    if (!hasRenderableTarget(target)) {
        return;
    }

    map.updateSize();

    const mapSize = map.getSize();

    if (!Array.isArray(mapSize) || mapSize.length !== 2 || mapSize.some((value) => !Number.isFinite(value) || value <= 0)) {
        return;
    }

    const previousZoom = typeof view.getZoom === "function" ? view.getZoom() : null;
    const previousResolution = typeof view.getResolution === "function" ? view.getResolution() : null;
    const previousCenter = typeof view.getCenter === "function" ? view.getCenter() : null;
    const shouldPreserveZoom = preserveZoom && (Number.isFinite(previousZoom) || Number.isFinite(previousResolution));

    if (shouldPreserveZoom) {
        return;
    }

    const resolvedCenter = isFiniteCoordinatePair(targetCenter)
        ? targetCenter
        : (isFiniteCoordinatePair(fallbackCenter) ? fallbackCenter : null);
    const finalCenter = resolvedCenter || (isFiniteCoordinatePair(previousCenter) ? previousCenter : null);

    const runFit = () => {
        try {
            view.fit(extent, {size: mapSize, duration: 0});

            if (finalCenter && typeof view.setCenter === "function") {
                view.setCenter(finalCenter);
            }
        }
        catch (error) {
            console.warn("Unable to fit assignment map extent", error);
        }
    };

    if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(runFit);
    }
    else {
        setTimeout(runFit, 0);
    }
}

export function transform4326to3857 (lon,lat){
    return transform([lon,lat],'EPSG:4326', 'EPSG:3857' )
}

export function setNewMapCenter(newcoords){
    map.getView().setCenter(newcoords);
    map.getView().setZoom(18)
}

export function createPointWKTfromCoords(lon,lat) {
    $(currentpanelwkt).text('POINT('+lon+' '+lat+')')
}

export function getExtentofAssignmentsAndPoints(options = {}){
    const {preserveView = false} = options;

    if (preserveView) {
        return;
    }
    const view = map.getView();
    const hasView = Boolean(view);
    const rotateCenter = getSourceCenter(rotateSource);
    const panelCenter = getPanelCenterFromState();
    const previousCenter = hasView && typeof view.getCenter === "function" ? view.getCenter() : null;
    const extent = buildCombinedExtent([
        rotateSource,
        assignmentsource,
        assignmentsource2021,
        assignmentsource2023,
        assignmentsource2025
    ]);

    const hasZoom = hasView && typeof view.getZoom === "function" && Number.isFinite(view.getZoom());
    const hasResolution = hasView && typeof view.getResolution === "function" && Number.isFinite(view.getResolution());

    scheduleViewFit(extent, {
        preserveZoom: hasZoom || hasResolution,
        preserveView,
        targetCenter: rotateCenter || panelCenter,
        fallbackCenter: panelCenter || previousCenter
    });
}
export function getExtentofPlaceAssignmentsAndPoints(options = {}){
    const {preserveView = false} = options;

    if (preserveView) {
        return;
    }
    const view = map.getView();
    const hasView = Boolean(view);
    const rotateCenter = getSourceCenter(rotateSource);
    const panelCenter = getPanelCenterFromState();
    const previousCenter = hasView && typeof view.getCenter === "function" ? view.getCenter() : null;
    const extent = buildCombinedExtent([
        rotateSource,
        placebasedassignmentsource
    ]);

    const hasZoom = hasView && typeof view.getZoom === "function" && Number.isFinite(view.getZoom());
    const hasResolution = hasView && typeof view.getResolution === "function" && Number.isFinite(view.getResolution());

    scheduleViewFit(extent, {
        preserveZoom: hasZoom || hasResolution,
        preserveView,
        targetCenter: rotateCenter || panelCenter,
        fallbackCenter: panelCenter || previousCenter
    });
}

export function getExtentOfSubFilteredPoints(){
    let extent = createEmpty();
    // console.log(filteredInventoryLayer)
    OLextend(extent,filteredInventoryLayer.getExtent())
    map.getView().fit(extent)
}

export function getH3Value(lat, lon, res){
    return geoToH3(lat, lon, res)
}